import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTheme } from "../../ThemeContext"; // Adjust the path as necessary
import { useFormik } from "formik";
import * as Yup from "yup";
import Project from "../Dropdowns/ProjectNames"; // Custom Project dropdown component
import { addMeeting } from "../../api/api"; // API call to add meeting
import { toast } from "react-toastify";

export default function AddMeeting({
  setEditing,
  handlePageUpdate,
  isEditing,
}) {
  const { theme } = useTheme(); // Get the theme from context
  const [open, setOpen] = useState(true);
  const cancelButtonRef = useRef(null);
  const [pname, setPname] = useState(""); // Selected project name

  // Validation schema for the form
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    date: Yup.date().required("Date is required"),
    time: Yup.string().required("Time is required"),
    agenda: Yup.string().required("Agenda is required"),
    description: Yup.string().required("Description is required"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      date: "",
      time: "",
      agenda: "",
      description: "",
      location:"",
      link: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const user = JSON.parse(localStorage.getItem("user"));

      // Combine date and time into a single Date object
      const [hours, minutes] = values.time.split(":");
      const mergedDateTime = new Date(values.date);
      mergedDateTime.setHours(hours, minutes);

      const data = {
        user_id: user._id,
        projectId: pname, // Use pname as project_id
        title: values.title,
        agenda: values.agenda,
        dateTime: mergedDateTime.toISOString(), // Ensure it's in ISO format
        description: values.description,
        meeting_link: values.link,
        location: values.location
      };
// console.log(data)
      try {
        await addMeeting(data); // Call the API to add meeting
        toast.success("Meeting Added Successfully");
        setOpen(false); // Close the modal on success
        handlePageUpdate(); // Trigger page update
      } catch (error) {
        console.error("Something went wrong:", error);
      }
    },
  });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-hidden rounded-md ${
                  theme === "dark"
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-900"
                } text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md`}
              >
                <div
                  className={`${
                    theme === "dark" ? "bg-gray-800" : "bg-white"
                  } px-4 pt-5 pb-4 sm:p-6 sm:pb-4`}
                >
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-lg py-4 text-center font-semibold leading-6"
                      >
                        Add Meeting
                      </Dialog.Title>
                      <form onSubmit={formik.handleSubmit}>
                        <div className="grid gap-4 mb-4 sm:grid-cols-2">
                          {/* Title Field */}
                          <div>
                            <label
                              htmlFor="title"
                              className="block mb-2 text-sm font-medium"
                            >
                              Meeting Title
                            </label>
                            <input
                              type="text"
                              name="title"
                              id="title"
                              value={formik.values.title}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 ${
                                theme === "dark"
                                  ? "bg-gray-700 text-white border-gray-600"
                                  : ""
                              }`}
                              placeholder="Enter meeting title"
                            />
                            {formik.touched.title && formik.errors.title && (
                              <div className="text-red-600 text-sm">
                                {formik.errors.title}
                              </div>
                            )}
                          </div>

                          {/* Agenda Field */}
                          <div>
                            <label
                              htmlFor="agenda"
                              className="block mb-2 text-sm font-medium"
                            >
                              Agenda
                            </label>
                            <input
                              name="agenda"
                              id="agenda"
                              value={formik.values.agenda}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 ${
                                theme === "dark"
                                  ? "bg-gray-700 text-white border-gray-600"
                                  : ""
                              }`}
                              placeholder="Enter meeting agenda"
                            />
                            {formik.touched.agenda && formik.errors.agenda && (
                              <div className="text-red-600 text-sm">
                                {formik.errors.agenda}
                              </div>
                            )}
                          </div>

                          {/* Date Field */}
                          <div>
                            <label
                              htmlFor="date"
                              className="block mb-2 text-sm font-medium"
                            >
                              Date
                            </label>
                            <input
                              type="date"
                              name="date"
                              id="date"
                              value={formik.values.date}
                              onChange={formik.handleChange}
                              onClick={(e) => e.target.showPicker()}
                              onBlur={formik.handleBlur}
                              className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 ${
                                theme === "dark"
                                  ? "bg-gray-700 text-white border-gray-600"
                                  : ""
                              }`}
                            />
                            {formik.touched.date && formik.errors.date && (
                              <div className="text-red-600 text-sm">
                                {formik.errors.date}
                              </div>
                            )}
                          </div>

                          {/* Time Field */}
                          <div>
                            <label
                              htmlFor="time"
                              className="block mb-2 text-sm font-medium"
                            >
                              Time
                            </label>
                            <input
                              type="time"
                              name="time"
                              id="time"
                              value={formik.values.time}
                              onChange={formik.handleChange}
                              onClick={(e) => e.target.showPicker()}
                              onBlur={formik.handleBlur}
                              className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 ${
                                theme === "dark"
                                  ? "bg-gray-700 text-white border-gray-600"
                                  : ""
                              }`}
                            />
                            {formik.touched.time && formik.errors.time && (
                              <div className="text-red-600 text-sm">
                                {formik.errors.time}
                              </div>
                            )}
                          </div>

                         

                          {/* Meeting Link */}
                          <div>
                            <label
                              htmlFor="link"
                              className="block mb-3 text-sm font-medium"
                            >
                              Meeting Link
                            </label>
                            <input
                              name="link"
                              id="link"
                              value={formik.values.link}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2 ${
                                theme === "dark"
                                  ? "bg-gray-700 text-white border-gray-600"
                                  : ""
                              }`}
                              placeholder="Enter meeting link"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="location"
                              className="block mb-3 text-sm font-medium"
                            >
                              Meeting Location
                            </label>
                            <input
                              name="location"
                              id="location"
                              value={formik.values.location}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2 ${
                                theme === "dark"
                                  ? "bg-gray-700 text-white border-gray-600"
                                  : ""
                              }`}
                              placeholder="Enter meeting location"
                            />
                          </div>
                           {/* Project Dropdown */}
                           <div className="col-span-2">
                            <Project
                              pname={pname}
                              handleChange={(e) => setPname(e.target.value)}
                            />
                          </div>
                          {/* Description */}
                          <div className="col-span-2">
                            <label
                              htmlFor="description"
                              className="block mb-2 text-sm font-medium"
                            >
                              Description
                            </label>
                            <textarea
                              name="description"
                              id="description"
                              value={formik.values.description}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 ${
                                theme === "dark"
                                  ? "bg-gray-700 text-white border-gray-600"
                                  : ""
                              }`}
                              placeholder="Enter meeting description"
                            />
                            {formik.touched.description &&
                              formik.errors.description && (
                                <div className="text-red-600 text-sm">
                                  {formik.errors.description}
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Buttons */}
                        <div className="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse justify-between">
                          <button
                            type="submit"
                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
                          >
                            {isEditing ? "Save" : "Add"}
                          </button>
                          <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto"
                            onClick={() => {
                              setOpen(false);
                              if (isEditing) setEditing(false);
                            }}
                            ref={cancelButtonRef}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
